import { useEffect, useRef } from 'react'

import { useActiveScanForNeed } from '@/utils/hooks/scans'
import { useTaskContext, type Task } from './TaskContext'

/**
 * Whether a task of our own already answers "is a scan running for this need?".
 *
 * A running or completed task does. An errored one does not: TaskContext rewrites
 * any task still running 20 minutes after it started to "Task was interrupted",
 * which is a guess about a job only the server can report on. Letting that guess
 * stand in for an answer strands every long scan behind a false terminal error,
 * because it also switches off the query that would have corrected it.
 */
function answersTheQuestion(task: Task | null): boolean {
  return !!task && !['error', 'cleaned_up'].includes(task.status)
}

/**
 * Pick up a scan this browser didn't start.
 *
 * Task state lives in localStorage and is written by whichever tab pressed the
 * button, so a scan started during onboarding, in another tab, on another device
 * or by a colleague is invisible here. Registering the running job in TaskContext
 * puts it on the same footing as a locally started one: the scan task hook
 * subscribes to its progress stream, the usual progress UI renders, and query
 * invalidation on completion happens as normal.
 *
 * Returns `isSettled` — whether we yet know if a scan is running, so callers can
 * hold off on saying "no matches" until the question has an answer.
 *
 * `poll` keeps asking every 15s rather than only on mount. Opt in from a page
 * watching one need; leave it off in a list, where it would cost one request per
 * row every 15s for as long as the org has no scan running.
 */
export function useAdoptActiveScan(
  token: string,
  fundingNeedId: number,
  { poll = false }: { poll?: boolean } = {},
) {
  const { startTask, findTaskByNeedId } = useTaskContext()
  const adoptedJobIds = useRef<Set<string>>(new Set())

  const needId = fundingNeedId ? String(fundingNeedId) : ''
  // A task of our own already answers the question, so stop asking the server.
  const ownTask = needId ? findTaskByNeedId(needId, 'scan') : null
  const trackedTask = answersTheQuestion(ownTask) ? ownTask : null

  const { activeScan, isFetched } = useActiveScanForNeed(
    token,
    fundingNeedId,
    !trackedTask,
    poll,
  )

  useEffect(() => {
    if (!activeScan || !needId) return
    if (adoptedJobIds.current.has(activeScan.job_id)) return
    if (answersTheQuestion(findTaskByNeedId(needId, 'scan'))) return

    adoptedJobIds.current.add(activeScan.job_id)
    startTask('scan', activeScan.job_id, needId)
  }, [activeScan, needId, findTaskByNeedId, startTask])

  return {
    activeScan,
    // A found-but-not-yet-registered scan is not a settled answer: startTask runs
    // in an effect, i.e. after the paint that first sees `activeScan`, so treating
    // it as settled paints one frame of "no matches" for a need that is mid-scan.
    isSettled: !!trackedTask || (isFetched && !activeScan),
  }
}
